// UNIVAPLAT-2549: Bulk-send confirmation modal — shows the recipient count resolved from the
// current admin-dashboard filters and triggers POST /v2/integrations/whatsapp/bulk-sends.
// Mirrors AdminWhatsappConfirmModal (single send), but carries the filter payload instead of a
// userId: the backend re-resolves recipients server-side from the filters at send time.
//
// NOTE (Epic UNIVAPLAT-2532, known non-blocking issue): `recipientCount` shown here is the
// dashboard's pagination.totalCount, which can UNDERSTATE the true recipient count when the
// careProgramme / currentWeek / firstLoginDateStart / firstLoginDateEnd filters are active
// (GET /v2/users applies those as an in-memory post-filter). The authoritative count is the
// `recipientCount` the backend returns after the send. Resolution is owned by the tech lead
// before the Epic merges — do not "fix" it here.
import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AdminWhatsappService} from '@shared/api/services/v2/admin-whatsapp.service';
import {AdminDashboardFilters} from '@shared/api/models/query/admin-user.model';
import {AdminWhatsappResultModal} from './admin-whatsapp-result.modal';

@Component({
    selector: 'cdss-admin-whatsapp-bulk-confirm-modal',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './admin-whatsapp-bulk-confirm.modal.html',
})
export class AdminWhatsappBulkConfirmModal {
    /** Admin-dashboard filter payload the backend will re-resolve recipients from. */
    @Input() filters!: AdminDashboardFilters;
    @Input() templateSid!: string;
    @Input() variableValues!: Record<string, string>;
    /** Recipient count shown to the admin (dashboard pagination.totalCount — see NOTE above). */
    @Input() recipientCount!: number;

    sending = false;

    constructor(
        public modal: NgbActiveModal,
        private adminWhatsappService: AdminWhatsappService,
        private modalService: NgbModal,
    ) {}

    back(): void { this.modal.dismiss('back'); }

    async send(): Promise<void> {
        this.sending = true;
        try {
            const response = await this.adminWhatsappService.sendBulkMessage({
                ...this.filters,
                templateSid: this.templateSid,
                variables: this.variableValues,
            });
            // Surface the BACKEND-resolved count — authoritative over the dashboard count.
            this.openResultModal(true, `Queued for ${response.recipientCount} recipient(s) (bulk send #${response.bulkSendId}).`);
        } catch (err: any) {
            // Use the structured API error if present; fall back to a safe generic message.
            // Never surface err?.message — that can expose the API URL from HttpErrorResponse.
            // err.error.error matches the backend's actual {error, exception} contract
            // (App\EventListener\ExceptionListener::setJsonResponse) — same convention used
            // app-wide, e.g. signin.component.ts, study-overview.component.ts (UNIVAPLAT-2532).
            const msg = err?.error?.error ?? 'An unexpected error occurred.';
            this.openResultModal(false, msg);
        } finally {
            this.sending = false;
        }
    }

    private openResultModal(success: boolean, statusMessage: string): void {
        // keyboard: false prevents ESC from dismissing the result modal, ensuring the user
        // clicks Close and the refresh signal propagates correctly (same as single-send flow).
        const resultRef = this.modalService.open(AdminWhatsappResultModal, {keyboard: false, centered: true});
        resultRef.componentInstance.success = success;
        resultRef.componentInstance.statusMessage = statusMessage;
        resultRef.result.then(r => {
            if (r === 'refresh') { this.modal.close('refresh'); }
        }).catch(() => {});
    }
}
